<template>
  <div>
    <DeviceListHeader
      :devices="devices"
      :defaultSort="sortKey"
      @add-device="openAddDeviceModal"
      @sorted="sortedDevices = $event"
    />

    <DeviceList
      :devices="sortedDevices"
      :isLoading="isLoading"
      :deviceStatsById="deviceStatsById"
      :isStatsLoading="isStatsLoading"
      :statsRangeLabel="statsRangeLabel"
      :statsComparisonLabel="statsComparisonLabel"
      @view="setDeviceForView"
      @edit="setDeviceForEdit"
      @delete="setDeviceForDelete"
    />

    <DeviceModal
      ref="deviceModal"
      :mode="deviceModalMode"
      :device="selectedDevice"
      :templates="templateList"
      @device-added="fetchData"
      @device-edited="fetchData"
    />

    <DeleteDeviceModal
      ref="deleteModal"
      :device="deviceToDelete"
      @deleted="fetchData"
    />

    <BootstrapModal
      ref="viewTemplateModal"
      modalId="viewTemplateModal"
      title="Template"
      closeText="Close"
      hideConfirmButton
    >
      <template #body>
        <div
          class="d-flex justify-content-center align-items-center"
          style="max-height: 70vh"
        >
          <img
            :src="this.adminUrl + (previewTemplate?.setting?.img_screen ?? '')"
            class="img-fluid"
            alt="Template"
          />
        </div>
      </template>
    </BootstrapModal>
  </div>
</template>

<script>
import DeviceModal from "@/components/Device/DeviceModal.vue";
import BootstrapModal from "@/components/Common/BootstrapModal.vue";
import DeleteDeviceModal from "@/components/Device/DeleteDeviceModal.vue";
import DeviceListHeader from "@/components/Device/DeviceListHeader.vue";
import DeviceList from "@/components/Device/DeviceList.vue";
import DeviceService from "@/services/deviceService.js";
import analyticsService from "@/services/analyticsService";

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - 7);

  return {
    from: formatDateForInput(fromDate),
    to: formatDateForInput(toDate),
  };
};

export default {
  emits: ["updateTitle"],
  components: {
    BootstrapModal,
    DeleteDeviceModal,
    DeviceList,
    DeviceModal,
    DeviceListHeader,
  },
  data() {
    return {
      sortKey: "id",
      sortedDevices: [],
      adminUrl: process.env.VUE_APP_TEMPLATE_THUMBNAIL_URL,
      isLoading: true,
      devices: [],
      deviceModalMode: 'add',
      selectedDevice: null,
      templateList: [],
      previewTemplate: null,
      deviceToDelete: null,
      isStatsLoading: false,
      deviceStatsById: {},
      statsRangeLabel: "",
      statsComparisonLabel: "",
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    openAddDeviceModal() {
      this.deviceModalMode = 'add';
      this.selectedDevice = null;
      this.$nextTick(() => {
        this.$refs.deviceModal.resetForm();
        this.$refs.deviceModal.show();
      });
    },
    setDeviceForView(device) {
      this.previewTemplate = device;
      this.$refs.viewTemplateModal.show();
    },
    setDeviceForEdit(device) {
      this.deviceModalMode = 'edit';
      this.selectedDevice = device;

      this.$nextTick(() => {
        this.$refs.deviceModal.show();
      });
    },
    setDeviceForDelete(device) {
      this.deviceToDelete = device;
      this.$refs.deleteModal.show();
    },
    async fetchData() {
      this.isLoading = true;
      this.isStatsLoading = true;
      const fallbackRange = getDefaultDateRange();

      const devicesTask = DeviceService.getDevices()
        .then((data) => {
          this.devices = data.devices;
          this.templateList = data.templates;
        })
        .catch((error) => {
          console.error("Error fetching devices.", error);
          this.devices = [];
          this.templateList = [];
        })
        .finally(() => {
          this.isLoading = false;
        });

      const statsTask = analyticsService
        .getRevenueTimeline({
          from: fallbackRange.from,
          to: fallbackRange.to,
          granularity: "day",
        })
        .then((payload) => {
          this.applyDeviceStats(payload, fallbackRange);
        })
        .catch((error) => {
          console.error("Error fetching device analytics stats.", error);
          this.deviceStatsById = {};
          this.statsRangeLabel = `${fallbackRange.from} -> ${fallbackRange.to}`;
          this.statsComparisonLabel = "Compared to previous period: N/A";
        })
        .finally(() => {
          this.isStatsLoading = false;
        });

      await Promise.allSettled([devicesTask, statsTask]);
    },
    applyDeviceStats(payload, fallbackRange) {
      const rows = Array.isArray(payload?.devices) ? payload.devices : [];
      const statsById = {};

      rows.forEach((row) => {
        if (row?.device_id === null || row?.device_id === undefined) {
          return;
        }

        statsById[String(row.device_id)] = row;
      });

      this.deviceStatsById = statsById;

      const range = payload?.range?.from && payload?.range?.to
        ? payload.range
        : fallbackRange;

      this.statsRangeLabel = `${range.from} -> ${range.to}`;

      const previous = payload?.trend_indicator?.previous_period;
      if (previous?.from && previous?.to) {
        this.statsComparisonLabel = `Compared to previous period: ${previous.from} -> ${previous.to}`;
      } else {
        this.statsComparisonLabel = "Compared to previous period: N/A";
      }
    },
    async downloadSettings(deviceId) {
      try {
        const response = await DeviceService.downloadSettings(deviceId);
        const blob = new Blob([response.data], { type: "application/gzip" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${deviceId}_sett.tar.gz`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download error:", err);
      }
    },
  },
};
</script>
